import { useEffect } from "react";
import UpperGrandLagoonHeroSection from "./components/HeroSection";
import UpperGrandLagoonStickyNav from "./components/StickyNav";
import UpperGrandLagoonProcessingTimeSection from "./components/ProcessingTimeSection";
import UpperGrandLagoonServicesSection from "./components/ServicesSection";
import UpperGrandLagoonLocalAreaSection from "./components/LocalAreaSection";
import UpperGrandLagoonTestimonialsSection from "./components/TestimonialsSection";
import UpperGrandLagoonAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import UpperGrandLagoonComparisonSection from "./components/ComparisonSection";
import UpperGrandLagoonMistakesSection from "./components/MistakesSection";
import UpperGrandLagoonHowItWorksSection from "./components/HowItWorksSection";
import UpperGrandLagoonFAQSection, { faqs } from "./components/FAQSection";
import UpperGrandLagoonHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import UpperGrandLagoonFinalCTA from "./components/FinalCTA";
import UpperGrandLagoonTravelTimingSection from "./components/TravelTimingSection";
import UpperGrandLagoonDestinationsSection from "./components/DestinationsSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function UpperGrandLagoonPage() {
  useEffect(() => {
    document.title = "Emergency Passport Upper Grand Lagoon, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/upper-grand-lagoon/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Upper Grand Lagoon, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving the Florida Panhandle and Emerald Coast.");
    setMeta("geo.placename", "Upper Grand Lagoon, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "30.1775;-85.7732");
    setMeta("ICBM", "30.1775, -85.7732");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Upper Grand Lagoon — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Upper Grand Lagoon",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Upper Grand Lagoon",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <UpperGrandLagoonHeroSection />
      <UpperGrandLagoonStickyNav />
      <UpperGrandLagoonProcessingTimeSection />
      <UpperGrandLagoonServicesSection />
      <UpperGrandLagoonLocalAreaSection />
      <UpperGrandLagoonTravelTimingSection />
      <UpperGrandLagoonDestinationsSection />
      <UpperGrandLagoonTestimonialsSection />
      <UpperGrandLagoonAcceptanceFacilitiesSection />
      <UpperGrandLagoonComparisonSection />
      <UpperGrandLagoonMistakesSection />
      <UpperGrandLagoonHowItWorksSection />
      <UpperGrandLagoonFAQSection />
      <UpperGrandLagoonHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/upper-grand-lagoon" state="FL" />
      <UpperGrandLagoonFinalCTA />
    </div>
  );
}